import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="slide-assistant"
//
// Owns the FAB drawer surface plus three internal views:
//   - chat:    the live conversation for the current session's task
//   - history: the list of prior conversations for the current subject
//   - archive: a single past conversation in read-only mode
//
// Each "drawer open" event creates a fresh AssistantTask via POST. While
// the drawer is open, re-clicking the same subject is a no-op; clicking a
// different subject creates a fresh task for that subject.
export default class extends Controller {
  static targets = [
    "drawer", "backdrop", "iframe", "menu", "title",
    "back", "historyButton", "historyBadge", "toast",
    "fragmentEditButton"
  ]
  static values = {
    taskCreatePattern:    String,   // /a/assistant_tasks/for___KIND__/__ID__
    intentCreatePattern:  String,   // /a/assistant_tasks/for_intent/__INTENT__
    chatUrlPattern:       String,   // /a/assistant_tasks/__ID__?embedded=1
    historyUrlPattern:    String,   // /a/assistant_tasks?embedded=1&subject_type=__TYPE__&subject_id=__ID__&exclude_id=__EXCLUDE__
    currentSubject:       { type: String, default: "" }, // "slide:123" or ""
    currentTaskId:        { type: String, default: "" },
    currentSubjectTitle:  { type: String, default: "" },
    historyCount:         { type: Number, default: 0 },
    view:                 { type: String, default: "chat" } // "chat" | "history" | "archive"
  }

  connect() {
    this.boundPageClick = this.handlePageClick.bind(this)
    this.boundKeydown = this.handleKeydown.bind(this)
    this.boundFragmentEnter = this.handleFragmentEnter.bind(this)
    this.boundFragmentLeave = this.handleFragmentLeave.bind(this)
    this.boundFragmentTap = this.handleFragmentTap.bind(this)
    this.boundIframeLoad = this.handleIframeLoad.bind(this)
    document.addEventListener("click", this.boundPageClick)
    document.addEventListener("keydown", this.boundKeydown)
    document.addEventListener("mouseover", this.boundFragmentEnter)
    document.addEventListener("mouseout", this.boundFragmentLeave)
    document.addEventListener("touchstart", this.boundFragmentTap, { passive: true })
    if (this.hasIframeTarget) {
      this.iframeTarget.addEventListener("load", this.boundIframeLoad)
    }
    this.activeFragment = null
    this.inFlightToken = 0
  }

  disconnect() {
    document.removeEventListener("click", this.boundPageClick)
    document.removeEventListener("keydown", this.boundKeydown)
    document.removeEventListener("mouseover", this.boundFragmentEnter)
    document.removeEventListener("mouseout", this.boundFragmentLeave)
    document.removeEventListener("touchstart", this.boundFragmentTap)
    if (this.hasIframeTarget) {
      this.iframeTarget.removeEventListener("load", this.boundIframeLoad)
    }
  }

  handlePageClick(event) {
    if (event.target.closest("a, button, input, textarea, select, label")) return
    const generic = event.target.closest(
      "[data-edit='row'][data-row-id], [data-edit='slide'][data-slide-id], [data-edit='column'][data-column-id], [data-edit='page'][data-page-id]"
    )
    if (generic) {
      event.preventDefault()
      const kind = generic.dataset.edit
      const id   = generic.dataset[`${kind}Id`]
      this.openFor(kind, id)
      return
    }
    const legacy = event.target.closest("[data-slide-edit][data-slide-id]")
    if (legacy) {
      event.preventDefault()
      this.openFor("slide", legacy.dataset.slideId)
    }
  }

  openAction(event) {
    event?.preventDefault()
    this.openMenu()
  }

  // Menu cards: Events / Announcements / This page
  pickIntent(event) {
    event?.preventDefault()
    const intent = event?.params?.intent
    if (!intent) return
    this.startIntent(intent)
  }

  pickPage(event) {
    event?.preventDefault()
    const pageId = event?.params?.pageId
    const pageTitle = event?.params?.pageTitle
    if (!pageId) return
    this.openFor("page", pageId, { title: pageTitle })
  }

  // Main entry: click on a section. Creates a fresh task unless the same
  // subject is already loaded.
  async openFor(kind, id, options = {}) {
    if (!id) return
    const subjectKey = `${kind}:${id}`
    if (this.drawerIsOpen() && this.currentSubjectValue === subjectKey && this.viewValue === "chat") {
      // Same subject re-click during open chat — no-op.
      this.markActive(kind, id)
      return
    }

    const url = this.taskCreatePatternValue
                  .replace("__KIND__", kind)
                  .replace("__ID__", encodeURIComponent(id))
    const data = await this.startSession(url)
    if (!data) {
      if (this.drawerIsOpen()) this.showErrorToast("Couldn't start a conversation. Please try again.")
      else console.warn("[slide-assistant] startSession failed for", subjectKey)
      return
    }

    this.openDrawer()
    this.showIframe()

    if (options.activeElement) {
      this.markActiveElement(options.activeElement)
    } else {
      this.markActive(kind, id)
    }

    this.currentSubjectValue = subjectKey
    this.currentSubjectTitleValue = options.title || this.titleFromKind(kind, id)
    this.currentTaskIdValue = String(data.task_id)
    this.historyCountValue = Number(data.history_count) || 0
    this.viewValue = "chat"

    let chatUrl = this.chatUrlPatternValue.replace("__ID__", encodeURIComponent(data.task_id))
    if (options.fragment) {
      chatUrl += `&fragment=${encodeURIComponent(options.fragment)}`
      if (options.fragmentText) chatUrl += `&fragment_text=${encodeURIComponent(options.fragmentText)}`
    }
    this.navigateIframe(chatUrl)
    this.renderHeader()
    if ((data.discarded_pending_count || 0) > 0) this.showToast(data.discarded_pending_count)
  }

  async startIntent(intent) {
    const url = this.intentCreatePatternValue.replace("__INTENT__", encodeURIComponent(intent))
    const data = await this.startSession(url)
    if (!data) {
      if (this.drawerIsOpen()) this.showErrorToast("Couldn't start a conversation. Please try again.")
      else console.warn("[slide-assistant] startSession failed for intent:", intent)
      return
    }
    this.openDrawer()
    this.showIframe()
    this.currentSubjectValue = `intent:${intent}`
    this.currentSubjectTitleValue = intent === "events" ? "Events" : "Announcements"
    this.currentTaskIdValue = String(data.task_id)
    this.historyCountValue = 0  // intent path is cross-subject; no scoped history to show
    this.viewValue = "chat"
    this.navigateIframe(this.chatUrlPatternValue.replace("__ID__", encodeURIComponent(data.task_id)))
    this.renderHeader()
  }

  async startSession(url) {
    const token = ++this.inFlightToken
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": csrfToken || ""
        },
        credentials: "same-origin"
      })
      if (!resp.ok) return null
      const data = await resp.json()
      if (token !== this.inFlightToken) return null // stale response
      return data
    } catch (_e) {
      return null
    }
  }

  // Clock icon — show the history list for the current subject.
  openHistory(event) {
    event?.preventDefault()
    if (!this.currentSubjectValue.includes(":")) return
    const [kind, id] = this.currentSubjectValue.split(":")
    const type = this.typeForKind(kind)
    if (!type) return
    const url = this.historyUrlPatternValue
                    .replace("__TYPE__", encodeURIComponent(type))
                    .replace("__ID__", encodeURIComponent(id))
                    .replace("__EXCLUDE__", encodeURIComponent(this.currentTaskIdValue))
    this.navigateIframe(url)
    this.viewValue = "history"
    this.renderHeader()
  }

  // Back arrow — pop the back-stack.
  back(event) {
    event?.preventDefault()
    // Reconcile viewValue with the iframe's live URL before acting.
    // When Turbo Drive intercepts a link click inside the iframe it performs
    // a fetch-based navigation that does NOT fire the iframe's `load` event,
    // so `handleIframeLoad` never gets a chance to update viewValue.
    // Reading contentWindow.location directly here gives us the ground truth.
    this.syncViewFromIframe()
    if (this.viewValue === "archive") {
      this.openHistory()
    } else if (this.viewValue === "history") {
      this.navigateIframe(this.chatUrlPatternValue.replace("__ID__", encodeURIComponent(this.currentTaskIdValue)))
      this.viewValue = "chat"
      this.renderHeader()
    }
  }

  // Sync viewValue with whatever URL the iframe actually loaded — covers
  // the case where the iframe navigates internally (history row click ->
  // archive show). We sniff archive=1 in the iframe's live location.
  handleIframeLoad() {
    if (!this.drawerIsOpen()) return  // ignore initial about:blank load
    this.syncViewFromIframe()
  }

  // Read the iframe's live location and update viewValue + renderHeader to
  // match. Safe to call at any time (swallows cross-origin / transient errors).
  // Used by handleIframeLoad (full-page loads) and back() (to reconcile state
  // after Turbo-Drive navigations inside the iframe, which do NOT fire load).
  syncViewFromIframe() {
    try {
      const loc = this.iframeTarget.contentWindow.location
      const search = new URLSearchParams(loc.search)
      if (search.get("archive") === "1") {
        if (this.viewValue !== "archive") {
          this.viewValue = "archive"
          this.renderHeader()
        }
      } else if (loc.pathname.endsWith("/assistant_tasks")) {
        // History list is /a/assistant_tasks (the index path); the chat path
        // is /a/assistant_tasks/:id, so endsWith distinguishes them.
        if (this.viewValue !== "history") {
          this.viewValue = "history"
          this.renderHeader()
        }
      }
      // chat path is /a/assistant_tasks/:id — already set by openFor/back.
    } catch (_e) {
      // Cross-origin (shouldn't happen for our own iframe) or transient state; ignore.
    }
  }

  renderHeader() {
    if (!this.hasTitleTarget) return
    const v = this.viewValue
    const subjectTitle = this.currentSubjectTitleValue || "Assistant"

    if (v === "chat") {
      this.titleTarget.innerHTML = `<i class="fa fa-wand-magic-sparkles mr-2"></i>${this.escapeHtml(subjectTitle)}`
      if (this.hasBackTarget) this.backTarget.hidden = true
      if (this.hasHistoryButtonTarget) {
        this.historyButtonTarget.hidden = (this.historyCountValue <= 0)
        this.historyButtonTarget.setAttribute("aria-label",
          `View past conversations (${this.historyCountValue})`)
      }
      if (this.hasHistoryBadgeTarget) {
        this.historyBadgeTarget.textContent = String(this.historyCountValue)
        this.historyBadgeTarget.hidden = (this.historyCountValue <= 0)
      }
    } else if (v === "history") {
      this.titleTarget.innerHTML = `<i class="fa fa-clock-rotate-left mr-2"></i>History — ${this.escapeHtml(subjectTitle)}`
      if (this.hasBackTarget) {
        this.backTarget.hidden = false
        // If focus was on the now-hidden history button, move it to the back arrow.
        if (this.hasHistoryButtonTarget && document.activeElement === this.historyButtonTarget) {
          this.backTarget.focus()
        }
      }
      if (this.hasHistoryButtonTarget) this.historyButtonTarget.hidden = true
    } else if (v === "archive") {
      this.titleTarget.innerHTML = `<i class="fa fa-clock-rotate-left mr-2"></i>${this.escapeHtml(subjectTitle)} <span class="ml-2 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">Archive</span>`
      if (this.hasBackTarget) this.backTarget.hidden = false
      if (this.hasHistoryButtonTarget) this.historyButtonTarget.hidden = true
    }
  }

  showToast(n) {
    if (!this.hasToastTarget) return
    const t = this.toastTarget
    const noun = n === 1 ? "pending change" : "pending changes"
    t.textContent = `Discarded ${n} ${noun} from a previous conversation.`
    t.hidden = false
    clearTimeout(this.toastTimer)
    this.toastTimer = setTimeout(() => { t.hidden = true }, 5000)
  }

  showErrorToast(message) {
    if (!this.hasToastTarget) return
    const t = this.toastTarget
    t.textContent = message
    t.hidden = false
    clearTimeout(this.toastTimer)
    this.toastTimer = setTimeout(() => { t.hidden = true }, 4000)
  }

  openMenu() {
    this.menuTarget.hidden = false
    this.iframeTarget.hidden = true
    this.viewValue = "chat"
    this.currentSubjectValue = ""
    this.currentSubjectTitleValue = "Assistant"
    this.historyCountValue = 0
    this.renderHeader()
    this.openDrawer()
  }

  // Force the iframe to navigate to `url`. The iframe's `src` attribute can
  // drift from its actual current URL when it navigates internally via a
  // link click (the attribute reflects only the last value assigned by JS).
  // Assigning a value identical to the stale attribute is a no-op, so we
  // use location.replace() which always navigates. Falls back to setting
  // .src on initial load when contentWindow is not yet available.
  navigateIframe(url) {
    const cw = this.iframeTarget.contentWindow
    if (cw && cw.location) {
      try {
        cw.location.replace(url)
        return
      } catch (_e) {
        // Cross-origin (shouldn't happen) or transient state; fall through.
      }
    }
    this.iframeTarget.src = url
  }

  showIframe(src) {
    if (src) this.navigateIframe(src)
    this.menuTarget.hidden = true
    this.iframeTarget.hidden = false
  }

  drawerIsOpen() { return !this.drawerTarget.hidden }

  openDrawer() {
    if (!this.drawerTarget.hidden) return
    this.previouslyFocused = document.activeElement
    this.drawerTarget.hidden = false
    this.backdropTarget.hidden = false
    document.body.classList.add("sh-fab-open")
    const closeBtn = this.drawerTarget.querySelector(".sh-fab-drawer-close")
    if (closeBtn) closeBtn.focus()
    this.boundTrap = this.trapFocus.bind(this)
    this.drawerTarget.addEventListener("keydown", this.boundTrap)
  }

  close(event) {
    event?.preventDefault()
    this.inFlightToken = (this.inFlightToken || 0) + 1  // invalidate any pending startSession response
    this.drawerTarget.hidden = true
    this.backdropTarget.hidden = true
    document.body.classList.remove("sh-fab-open")
    this.markActive(null, null)
    this.currentSubjectValue = ""
    this.currentTaskIdValue = ""
    this.viewValue = "chat"
    this.historyCountValue = 0
    if (this.hasToastTarget) this.toastTarget.hidden = true
    if (this.boundTrap) {
      this.drawerTarget.removeEventListener("keydown", this.boundTrap)
      this.boundTrap = null
    }
    this.previouslyFocused?.focus?.()
    this.previouslyFocused = null
  }

  handleKeydown(event) {
    if (event.key === "Escape" && !this.drawerTarget.hidden) this.close()
  }

  markActiveElement(el) {
    document.querySelectorAll("[data-assistant-active]").forEach((other) => other.removeAttribute("data-assistant-active"))
    el.setAttribute("data-assistant-active", "true")
  }

  markActive(kind, id) {
    document.querySelectorAll("[data-assistant-active]").forEach((el) => el.removeAttribute("data-assistant-active"))
    if (!kind || !id) return
    const idAttr = `data-${kind}-id`
    document.querySelectorAll(`[data-edit="${kind}"][${idAttr}="${id}"]`).forEach((el) => el.setAttribute("data-assistant-active", "true"))
    if (kind === "slide") {
      document.querySelectorAll(`[data-slide-edit][data-slide-id="${id}"]`).forEach((el) => el.setAttribute("data-assistant-active", "true"))
    }
  }

  typeForKind(kind) {
    switch (kind) {
      case "slide":  return "Slide"
      case "row":    return "Row"
      case "column": return "Column"
      case "page":   return "Page"
      default:       return null
    }
  }

  titleFromKind(kind, id) {
    const map = { slide: "section", row: "row", column: "column", page: "page" }
    return map[kind] ? `${map[kind].charAt(0).toUpperCase()}${map[kind].slice(1)} ${id}` : "Assistant"
  }

  // ---- Fragment hover overlay (unchanged from prior version) ----
  handleFragmentEnter(event) {
    if (!this.hasFragmentEditButtonTarget) return
    const overlay = this.fragmentEditButtonTarget
    if (event.target.closest && event.target.closest(".sh-frag-edit-overlay") === overlay) {
      this.cancelHideOverlay()
      return
    }
    const frag = event.target.closest("[data-edit='fragment'][data-slide-id][data-fragment]")
    if (!frag) return
    this.cancelHideOverlay()
    this.positionFragmentEditOverlay(frag)
  }

  handleFragmentTap(event) {
    if (!this.hasFragmentEditButtonTarget) return
    if (this.drawerTarget.hidden) return
    const touch = event.touches?.[0]
    if (!touch) return
    const el = document.elementFromPoint(touch.clientX, touch.clientY)
    const frag = el?.closest?.("[data-edit='fragment'][data-slide-id][data-fragment]")
    if (!frag) return
    this.cancelHideOverlay()
    this.positionFragmentEditOverlay(frag)
  }

  handleFragmentLeave(event) {
    if (!this.hasFragmentEditButtonTarget) return
    const related = event.relatedTarget
    const overlay = this.fragmentEditButtonTarget
    const stillOnFragment = related && related.closest && related.closest("[data-edit='fragment'][data-slide-id][data-fragment]")
    const onOverlay = related && (related === overlay || (overlay.contains && overlay.contains(related)))
    if (stillOnFragment || onOverlay) return
    this.scheduleHideOverlay()
  }

  scheduleHideOverlay() {
    this.cancelHideOverlay()
    this.hideTimer = setTimeout(() => {
      if (this.hasFragmentEditButtonTarget) this.fragmentEditButtonTarget.hidden = true
      this.activeFragment = null
      this.hideTimer = null
    }, 250)
  }

  cancelHideOverlay() {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer)
      this.hideTimer = null
    }
  }

  positionFragmentEditOverlay(frag) {
    const overlay = this.fragmentEditButtonTarget
    const rect = frag.getBoundingClientRect()
    overlay.style.left = `${window.scrollX + rect.right + 8}px`
    overlay.style.top = `${window.scrollY + rect.top - 4}px`
    overlay.dataset.slideId = frag.dataset.slideId
    overlay.dataset.fragment = frag.dataset.fragment
    overlay.hidden = false
    this.activeFragment = frag
  }

  editFragment(event) {
    event?.preventDefault()
    const overlay = event.currentTarget
    const slideId = overlay.dataset.slideId
    const fragmentName = overlay.dataset.fragment
    if (!slideId || !fragmentName) return
    const visibleText = this.activeFragment?.textContent?.replace(/\s+/g, " ").trim() || ""
    this.openFor("slide", slideId, {
      fragment: fragmentName,
      fragmentText: visibleText,
      activeElement: this.activeFragment
    })
    overlay.hidden = true
  }

  trapFocus(event) {
    if (event.key !== "Tab") return
    const focusables = this.drawerTarget.querySelectorAll(
      'a[href], button:not([disabled]), textarea, input, select, iframe, [tabindex]:not([tabindex="-1"])'
    )
    if (focusables.length === 0) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault(); last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus()
    }
  }

  escapeHtml(s) {
    const div = document.createElement("div")
    div.textContent = s == null ? "" : String(s)
    return div.innerHTML
  }
}
